import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { noteCategories, getNonFeaturedArticlesByCategory } from '@/mocks/notes';

export default function CategorySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-[#f5f0e8]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Section Label */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-6 h-px bg-[#b08060]"></div>
          <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">所有分類</span>
        </div>
        <h2
          className={`font-serif-tc text-[#2c2420] mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
        >
          依你的需求，找到適合的文章
        </h2>

        <div className={`space-y-16 transition-all duration-700 delay-100 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          {noteCategories.map((cat) => {
            const articles = getNonFeaturedArticlesByCategory(cat.id);
            return (
              <div key={cat.id} id={cat.id}>
                {/* Category Header */}
                <div className="flex items-start justify-between mb-7 flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-full"
                      style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
                    >
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3
                        className="font-serif-tc text-[#2c2420] leading-tight"
                        style={{ fontSize: '1.375rem' }}
                      >
                        {cat.label}
                      </h3>
                      <p className="text-[0.9375rem] text-[#7a6e62] mt-0.5">{cat.description}</p>
                    </div>
                  </div>
                  <Link
                    to={`/notes?category=${cat.id}`}
                    className="text-[1rem] text-[#7a6248] font-medium flex items-center gap-1.5 hover:gap-3 transition-all whitespace-nowrap cursor-pointer"
                  >
                    查看全部文章 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                {/* Article Cards — 每類顯示 2 篇，不與精選文章重複 */}
                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {articles.slice(0, 2).map((article) => (
                      <Link
                        key={article.slug}
                        to={`/notes/${article.slug}`}
                        className="group block bg-white rounded-xl border border-[#ede5d8] hover:border-[#c8b89a] overflow-hidden transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative h-64 sm:h-72 overflow-hidden">
                          <img
                            src={article.coverImage}
                            alt={article.title}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                          />
                        </div>
                        <div className="p-5">
                          <h4
                            className="font-serif-tc text-[#2c2420] leading-[1.4] mb-2.5 group-hover:text-[#7a6248] transition-colors"
                            style={{ fontSize: '1rem' }}
                          >
                            {article.title}
                          </h4>
                          <p className="text-[0.875rem] leading-[1.65] text-[#7a6e62] line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-3 mt-4 text-[0.8125rem] text-[#a89880]">
                            <span>{article.publishedAt}</span>
                            <span>·</span>
                            <span>約 {article.readTime} 分鐘</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[0.9375rem] text-[#a89880]">
                    目前沒有更多文章
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}